import { generatePath } from 'react-router-dom';

import type { GetAuthTokenResponse } from 'application-types';
import { appUrl, routes } from 'client-config';
import { authorizationHeader } from 'lane-shared/helpers/formatters';

import makeFileDownload from 'helpers/makeFileDownload';

function parseFileNameFromContentDisposition(
  contentDisposition: string | null
): string | undefined {
  if (!contentDisposition) {
    return undefined;
  }

  const fileNameMatch =
    /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(contentDisposition);
  const rawFileName = fileNameMatch?.[1];

  if (!rawFileName) {
    return undefined;
  }

  try {
    return decodeURIComponent(rawFileName);
  } catch {
    return rawFileName;
  }
}

async function readErrorMessageFromResponse(response: Response): Promise<string> {
  const text = await response.text();

  try {
    const body = JSON.parse(text) as { error?: string; message?: string };
    return body.error || body.message || text || response.statusText;
  } catch {
    return text || response.statusText;
  }
}

export type DownloadInspectionReportPdfParams = {
  instanceId: string;
  getAuthToken: GetAuthTokenResponse;
};

export async function downloadInspectionReportPdf({
  instanceId,
  getAuthToken,
}: DownloadInspectionReportPdfParams): Promise<void> {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Missing authentication token');
  }

  const url = `${appUrl}${generatePath(routes.downloadInspectionPdf, {
    instanceId,
  })}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: authorizationHeader(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    const message = await readErrorMessageFromResponse(response);
    throw new Error(message);
  }

  const blob = await response.blob();
  const fileType = response.headers.get('content-type') || 'application/pdf';
  const fileName =
    parseFileNameFromContentDisposition(
      response.headers.get('content-disposition')
    ) ?? 'inspection-report.pdf';

  makeFileDownload({
    blob,
    name: fileName,
    type: fileType,
  });
}
